/**
 * Third-Party Licenses Page — Route Module (#8914)
 *
 * Renders the public client bundle's OWN third-party notices: every npm
 * package that contributes bytes to the bundle, every crate and vendored
 * dataset compiled into the domain WebAssembly modules (OpenCC's table among
 * them — the Apache-2.0 §4(a) obligation this page discharges), the Rust
 * toolchain row and any component embedded in another package's dist. The
 * list is not written here: it is derived in the bundle action from the
 * bundle's own metafile and FETCHED lazily (`THIRD_PARTY_NOTICES_URL`), so no
 * other page pays a byte for it and the page cannot drift from what ships.
 *
 * Not indexable (`marketing/licenses` in manifests/routes.yaml): a licence
 * list is no search landing page.
 */
import { noIndexRoute } from '@/shared/lib/seo/robots';
import { plaintextString } from '@letsgethome/types/branded';
import { PageContainer } from '@letsgethome/ui/page-container';
import { Prose } from '@letsgethome/ui/Prose';
import { useEffect, useState } from 'react';

import { SeoHead } from '../../../../shared/components/SeoHead';
import { useTranslations } from '../../../../shared/i18n/useTranslations';
import {
  groupNotices,
  parseNotices,
  THIRD_PARTY_NOTICES_URL,
  type NoticeRow,
  type ThirdPartyNotices,
} from '../components/third-party-notices';

type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'ready'; readonly notices: ThirdPartyNotices };

function useThirdPartyNotices(): LoadState {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  useEffect(() => {
    let live = true;
    fetch(THIRD_PARTY_NOTICES_URL)
      .then((res) => (res.ok ? res.json() : null))
      .then((json: unknown) => {
        const notices = parseNotices(json);
        if (live) setState(notices ? { kind: 'ready', notices } : { kind: 'unavailable' });
      })
      .catch(() => {
        if (live) setState({ kind: 'unavailable' });
      });
    return () => {
      live = false;
    };
  }, []);
  return state;
}

// Package names, versions, SPDX ids, authors and licence texts are DATA shown
// verbatim — never translated — so each passes the one sanctioned plaintext mint.
function NoticeEntry({ row, texts }: { row: NoticeRow; texts: ThirdPartyNotices['texts'] }) {
  const t = useTranslations('licenses');
  return (
    <li>
      <strong>{plaintextString(row.version ? `${row.name} ${row.version}` : row.name)}</strong>{' '}
      <code>{plaintextString(row.licence)}</code>
      {row.via && row.via.length > 0 ? (
        <p className="text-muted-foreground">{t('via', { via: row.via.join(', ') })}</p>
      ) : null}
      {row.author ? (
        <p className="text-muted-foreground">{plaintextString(`© ${row.author}`)}</p>
      ) : null}
      {row.copyright ? (
        <p className="text-muted-foreground">{plaintextString(row.copyright)}</p>
      ) : null}
      {/* Plain text, never a link: the tracker is private, so a link would
          404 for every public visitor. */}
      {row.pending ? <p>{t('pending', { issue: row.pending })}</p> : null}
      {row.texts.map((key) => (
        <details key={key}>
          <summary>{t('text')}</summary>
          <pre className="whitespace-pre-wrap text-xs">{plaintextString(texts[key] ?? '')}</pre>
        </details>
      ))}
    </li>
  );
}

export function Component() {
  const t = useTranslations('licenses');
  const tLegal = useTranslations('legal');
  const tCommon = useTranslations('common');
  const state = useThirdPartyNotices();

  return (
    <>
      <SeoHead
        title={t('seo.title')}
        description={t('seo.description')}
        robots={noIndexRoute('marketing/licenses')}
      />
      <PageContainer size="4xl" className="py-8 sm:px-6 lg:px-8">
        <Prose>
          <h1>{tLegal('licenses')}</h1>
          <p>{t('intro')}</p>
          {state.kind === 'loading' ? <p role="status">{tCommon('loading')}</p> : null}
          {state.kind === 'unavailable' ? (
            <div role="alert">
              <h2>{t('empty.title')}</h2>
              <p>{t('empty.description')}</p>
            </div>
          ) : null}
          {state.kind === 'ready'
            ? groupNotices(state.notices).map(({ group, rows }) => (
                <section key={group}>
                  <h2>{t(`section.${group}`)}</h2>
                  <ul>
                    {rows.map((row) => (
                      <NoticeEntry
                        key={`${row.source}:${row.name}@${row.version}`}
                        row={row}
                        texts={state.notices.texts}
                      />
                    ))}
                  </ul>
                </section>
              ))
            : null}
        </Prose>
      </PageContainer>
    </>
  );
}
